import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext.jsx';
import {
  jetGreeting,
  jetSuggestions,
  replyToJet,
} from '../../lib/jetReplies.js';
import {
  askJetAiChat,
  jetAiChatMode,
  shouldUseJetAiChat,
} from '../../lib/jetLegalChat.js';
import { isJetSmalltalk } from '../../lib/jetSmalltalk.js';
import {
  applyJetIntakeExtraction,
  isJetIntakeActive,
} from '../../lib/jetIntakeBridge.js';
import {
  extractEvidenceFromUploads,
  extractionHasAnyField,
  formatExtractedAmount,
} from '../../lib/evidenceExtract.js';
import { disputeLabel } from '../../lib/disputeTypes.js';
import { isCreateDemandPath } from '../../lib/jetIntakeNudge.js';
import {
  jetAnswerToMessages,
  jetTypingDelayMs,
} from '../../lib/jetVoice.js';
import { JET_OPEN_EVENT } from '../../lib/jetBridge.js';
import jetStageImg from '../../assets/jet/jet-stage.png';
import './jet.css';

const HIDDEN_KEY = 'demandjet.jet.hidden';

function readSessionFlag(key) {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeSessionFlag(key, value) {
  try {
    if (value) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  } catch {
    /* session storage can be blocked */
  }
}

function nextId() {
  return `jet-msg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function summarizeExtraction(fields = {}) {
  const address = [fields.debtorStreet, fields.debtorCity, fields.debtorState, fields.debtorPostalCode]
    .filter(Boolean)
    .join(', ');
  const rows = [
    fields.debtorCompany && `Company: ${fields.debtorCompany}`,
    fields.debtorName && `Contact: ${fields.debtorName}`,
    fields.debtorEmail && `Email: ${fields.debtorEmail}`,
    address && `Address: ${address}`,
    fields.amountOwed != null && `Amount: $${formatExtractedAmount(fields.amountOwed)}`,
    fields.externalInvoiceId && `Invoice: ${fields.externalInvoiceId}`,
    fields.disputeType && `Dispute: ${disputeLabel(fields.disputeType)}`,
  ].filter(Boolean);
  return rows.length ? rows.join('\n') : 'No clear fields found yet.';
}

function buildJetChatHistory(messages = []) {
  return (Array.isArray(messages) ? messages : [])
    .filter((item) => (item.from === 'user' || item.from === 'jet') && (item.text || item.extraction?.summary))
    .slice(-8)
    .map((item) => ({
      role: item.from === 'user' ? 'user' : 'assistant',
      text: [item.text, item.extraction?.summary && `Extracted fields:\n${item.extraction.summary}`]
        .filter(Boolean)
        .join('\n')
        .slice(0, 400),
    }));
}

export default function JetAssistant() {
  const { user } = useAuth();
  const location = useLocation();
  const role = String(user?.role || 'CLIENT').toUpperCase();
  const onIntake = isCreateDemandPath(location.pathname) && role === 'CLIENT';
  const titleId = useId();
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const logRef = useRef(null);
  const waveTimer = useRef(null);
  const replyTimer = useRef(null);
  const speakTimer = useRef(null);
  const intakeTipShown = useRef(false);
  const priorTopicRef = useRef('');
  const hadRecentSmalltalkRef = useRef(false);
  const bubbleTimers = useRef([]);

  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => readSessionFlag(HIDDEN_KEY));
  const [mood, setMood] = useState('idle');
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const [messages, setMessages] = useState(() => [
    { id: 'greet', from: 'jet', ...jetGreeting(role) },
  ]);
  const askRef = useRef(null);
  const openPanelRef = useRef(null);

  useEffect(() => {
    return () => {
      clearTimeout(waveTimer.current);
      clearTimeout(replyTimer.current);
      clearTimeout(speakTimer.current);
      bubbleTimers.current.forEach((id) => clearTimeout(id));
      bubbleTimers.current = [];
    };
  }, []);

  useEffect(() => {
    function onBridgeOpen(event) {
      const prompt = String(event?.detail?.prompt || '').trim();
      openPanelRef.current?.();
      if (prompt) {
        window.setTimeout(() => askRef.current?.(prompt), 80);
      }
    }
    window.addEventListener(JET_OPEN_EVENT, onBridgeOpen);
    return () => window.removeEventListener(JET_OPEN_EVENT, onBridgeOpen);
  }, []);

  useEffect(() => {
    if (!logRef.current) return;
    logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, pending, open]);

  useEffect(() => {
    if (!onIntake) {
      intakeTipShown.current = false;
      return;
    }
    if (!open || intakeTipShown.current) return;
    intakeTipShown.current = true;
    setMessages((current) => {
      const already = current.some((item) => item.id === 'intake-tip');
      if (already) return current;
      return [
        ...current,
        { id: 'intake-tip', from: 'jet', ...jetGreeting(role, { onIntake: true }) },
      ];
    });
  }, [onIntake, open, role]);

  function playWave() {
    clearTimeout(waveTimer.current);
    setMood('wave');
    waveTimer.current = setTimeout(() => {
      setMood((current) => (current === 'wave' ? 'idle' : current));
    }, 1900);
  }

  function openPanel() {
    setHidden(false);
    writeSessionFlag(HIDDEN_KEY, false);
    setOpen(true);
    playWave();
    setTimeout(() => inputRef.current?.focus(), 40);
  }
  openPanelRef.current = openPanel;

  function closePanel() {
    clearTimeout(waveTimer.current);
    setOpen(false);
    setMood('idle');
  }

  /** Close the chat and put Jet away for this browser session. */
  function closeJet() {
    clearTimeout(waveTimer.current);
    clearTimeout(replyTimer.current);
    clearTimeout(speakTimer.current);
    clearBubbleTimers();
    setOpen(false);
    setMood('idle');
    setPending(false);
    setHidden(true);
    writeSessionFlag(HIDDEN_KEY, true);
  }

  function restoreJet() {
    setHidden(false);
    writeSessionFlag(HIDDEN_KEY, false);
    openPanel();
  }

  function finishSpeak() {
    setPending(false);
    setMood('speak');
    speakTimer.current = setTimeout(() => {
      setMood((current) => (current === 'speak' ? 'listen' : current));
    }, 1500);
  }

  function clearBubbleTimers() {
    bubbleTimers.current.forEach((id) => clearTimeout(id));
    bubbleTimers.current = [];
  }

  function deliverJetAnswer(answer) {
    const chunks = jetAnswerToMessages(answer, nextId);
    if (!chunks.length) {
      finishSpeak();
      return;
    }
    priorTopicRef.current = String(answer.text || '').slice(0, 160);
    let delay = 0;
    chunks.forEach((chunk, index) => {
      delay += index === 0 ? jetTypingDelayMs(chunk.text) : Math.min(900, jetTypingDelayMs(chunk.text) * 0.65);
      const timer = setTimeout(() => {
        setMessages((current) => [...current, chunk]);
        if (index === chunks.length - 1) finishSpeak();
        else setMood('think');
      }, delay);
      bubbleTimers.current.push(timer);
    });
  }

  function ask(raw) {
    const text = String(raw || '').trim();
    if (!text || pending) return;
    clearTimeout(waveTimer.current);
    clearTimeout(replyTimer.current);
    clearTimeout(speakTimer.current);
    clearBubbleTimers();
    setDraft('');
    setPending(true);
    setMood('think');
    setMessages((current) => [...current, { id: nextId(), from: 'user', text, links: [] }]);

    const hadRecentSmalltalk = hadRecentSmalltalkRef.current;
    const hasExtraction = messages.some((item) => item.extraction?.summary);
    const chatContext = { onIntake, hadRecentSmalltalk, hasExtraction };
    if (isJetSmalltalk(text) || jetAiChatMode(text, chatContext) === 'chat') {
      hadRecentSmalltalkRef.current = true;
    } else {
      hadRecentSmalltalkRef.current = false;
    }

    if (shouldUseJetAiChat(text, role, chatContext)) {
      const history = buildJetChatHistory(messages);
      replyTimer.current = setTimeout(() => {
        void (async () => {
          try {
            const answer = await askJetAiChat({
              message: text,
              role,
              priorTopic: priorTopicRef.current,
              history,
              hadRecentSmalltalk,
            });
            deliverJetAnswer(answer);
          } catch (err) {
            deliverJetAnswer({
              text:
                err?.message ||
                'I couldn’t get to that one just now. Try Messages if you need an attorney, or ask me about pricing / sending a demand.',
              links: [{ label: 'Messages', to: '/client/messages' }],
            });
          }
        })();
      }, 280);
      return;
    }

    replyTimer.current = setTimeout(() => {
      const answer = replyToJet(text, role, { onIntake });
      deliverJetAnswer(answer);
    }, jetTypingDelayMs(text) * 0.45);
  }
  askRef.current = ask;

  async function handleEvidenceFiles(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length || pending) return;
    if (!onIntake || !isJetIntakeActive()) {
      setMessages((current) => [
        ...current,
        {
          id: nextId(),
          from: 'jet',
          text: 'Open Create Demand first, then upload an invoice here and I’ll fill the form.',
          links: [{ label: 'New demand', to: '/client/demands/new' }],
        },
      ]);
      return;
    }

    clearTimeout(waveTimer.current);
    clearTimeout(replyTimer.current);
    clearTimeout(speakTimer.current);
    clearBubbleTimers();
    setPending(true);
    setMood('think');
    const names = files.map((file) => file.name).join(', ');
    setMessages((current) => [
      ...current,
      { id: nextId(), from: 'user', text: `Uploaded: ${names}`, links: [] },
    ]);

    try {
      const fields = await extractEvidenceFromUploads({
        fileNames: files.map((file) => file.name),
        files,
      });
      const usable = extractionHasAnyField(fields);
      const modelLabel = fields.model ? ` (${fields.model})` : '';
      const notConfigured =
        fields.errorCode === 'OPENAI_NOT_CONFIGURED' || fields.openaiConfigured === false;
      const bareUnauthorized = /^unauthorized$/i.test(String(fields.error || '').trim());
      const invalidKey =
        fields.errorCode === 'OPENAI_INVALID_KEY' ||
        (bareUnauthorized && fields.errorCode !== 'UNAUTHORIZED');
      const sessionExpired = fields.errorCode === 'UNAUTHORIZED';
      const failureHint = notConfigured
        ? 'OpenAI isn’t configured on the Worker yet — set OPENAI_API_KEY (and optional OPENAI_MODEL) on Workers → demandjet-api → Settings → Variables and Secrets, then Deploy that Worker (not Pages).'
        : invalidKey
          ? 'OpenAI rejected the API key. Re-paste OPENAI_API_KEY on Worker demandjet-api (no quotes or spaces), confirm the key is active in OpenAI, then Deploy the Worker.'
          : sessionExpired
            ? 'Your session expired or isn’t authorized. Sign out and sign back in, then try the upload again.'
            : fields.error
              ? String(fields.error).slice(0, 220)
              : 'Try a clearer invoice PDF or photo.';
      const standAlone = notConfigured || invalidKey || sessionExpired;
      setMessages((current) => [
        ...current,
        {
          id: nextId(),
          from: 'jet',
          text: usable
            ? `Here’s what OpenAI pulled${modelLabel}. Review, then apply to the form.`
            : standAlone
              ? failureHint
              : `OpenAI couldn’t extract clear fields from that upload. ${failureHint}`,
          links: [],
          extraction: usable
            ? {
                fields,
                files,
                summary: summarizeExtraction(fields),
              }
            : null,
        },
      ]);
    } catch (err) {
      setMessages((current) => [
        ...current,
        {
          id: nextId(),
          from: 'jet',
          text: err?.message || 'I couldn’t read that evidence. Try again with a clearer invoice or PDF.',
          links: [],
        },
      ]);
    } finally {
      finishSpeak();
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function applyExtractionMessage(message) {
    const payload = message?.extraction;
    if (!payload?.fields) return;
    const ok = await applyJetIntakeExtraction({
      fields: payload.fields,
      files: payload.files || [],
    });
    setMessages((current) => [
      ...current,
      {
        id: nextId(),
        from: 'jet',
        text: ok
          ? 'Applied to the form. Review the fields, then send when you’re ready.'
          : 'I couldn’t reach the Create Demand form. Stay on that page and try Apply again.',
        links: [],
      },
    ]);
    playWave();
  }

  function onComposerKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      ask(draft);
    }
  }

  function onPanelKeyDown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeJet();
      return;
    }
    if (event.key !== 'Tab' || !panelRef.current) return;
    const focusable = [...panelRef.current.querySelectorAll('button, a[href], input, textarea')].filter(
      (node) => !node.disabled
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  useEffect(() => {
    if (!open || hidden) return undefined;
    function onPointerDown(event) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (panelRef.current?.contains(target)) return;
      // Footer Jet controls also open chat — don't treat them as outside clicks.
      if (target instanceof Element && target.closest('.list-pagination-footer')) return;
      closePanel();
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open, hidden]);

  const suggestions = jetSuggestions(role, { onIntake });

  // Session-hidden: no floating chrome. Footer (or restore) reopens via openPanel.
  if (hidden) {
    return (
      <div className="jet-assistant jet-assistant-hidden">
        <button
          type="button"
          className="jet-restore"
          onClick={restoreJet}
          aria-label="Open Jet, DemandJet’s AI assistant"
        >
          Ask Jet
        </button>
      </div>
    );
  }

  if (!open) {
    return null;
  }

  return (
    <div className="jet-assistant is-open">
      <section
        ref={panelRef}
        className="jet-panel"
        role="dialog"
        aria-labelledby={titleId}
        onKeyDown={onPanelKeyDown}
      >
        <header className="jet-panel-header">
          <div className="jet-brand-banner">
            <div className="jet-brand-mark">
              <h2 id={titleId} className="jet-brand-wordmark">
                JET
              </h2>
              <p className="jet-brand-eyebrow">THE DEMANDJET AI</p>
            </div>
            <div className="jet-brand-rule" aria-hidden="true" />
            <div className="jet-brand-pitch">
              <p>
                Jet is your always-on AI assistant, helping you find, connect,
                and convert demand — faster.
              </p>
              <span className="jet-brand-accent" aria-hidden="true" />
            </div>
          </div>
          <button
            type="button"
            className="jet-panel-close"
            onClick={closeJet}
            aria-label="Close Jet"
            title="Close Jet"
          >
            ×
          </button>
        </header>

        <div className="jet-stage" aria-hidden="true">
          <img
            className="jet-stage-figure"
            src={jetStageImg}
            alt=""
            decoding="async"
            draggable={false}
          />
        </div>

        <div className="jet-log" ref={logRef} aria-live="polite">
          {messages.map((message) => (
            <article
              key={message.id}
              className={message.from === 'user' ? 'jet-bubble is-user' : 'jet-bubble is-jet'}
            >
              <p>{message.text}</p>
              {message.extraction?.summary ? (
                <div className="jet-extract-card">
                  <pre>{message.extraction.summary}</pre>
                  <button
                    type="button"
                    className="jet-extract-apply"
                    onClick={() => applyExtractionMessage(message)}
                    disabled={pending}
                  >
                    Apply to form
                  </button>
                </div>
              ) : null}
              {message.links?.length ? (
                <div className="jet-links">
                  {message.links.map((link) => (
                    <Link key={link.to} to={link.to} onClick={closePanel}>
                      {link.label}
                    </Link>
                  ))}
                </div>
              ) : null}
            </article>
          ))}
          {pending ? (
            <div className="jet-typing" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
          ) : null}
        </div>

        <div className="jet-suggestions" aria-label="Suggested questions">
          {suggestions.map((suggestion) => (
            <button key={suggestion} type="button" onClick={() => ask(suggestion)} disabled={pending}>
              {suggestion}
            </button>
          ))}
        </div>

        <form
          className="jet-composer"
          onSubmit={(event) => {
            event.preventDefault();
            ask(draft);
          }}
        >
          {onIntake ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="jet-file-input"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.csv,.json,.md,image/*,text/*"
                multiple
                onChange={(event) => handleEvidenceFiles(event.target.files)}
                aria-label="Upload evidence for Jet"
                disabled={pending}
              />
              <button
                type="button"
                className="jet-attach"
                disabled={pending}
                onClick={() => fileInputRef.current?.click()}
                aria-label="Attach evidence for Jet to read"
                title="Attach invoice or evidence"
              >
                +
              </button>
            </>
          ) : null}
          <input
            ref={inputRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onComposerKeyDown}
            onFocus={() => {
              setMood((current) => (current === 'idle' || current === 'wave' ? 'listen' : current));
            }}
            placeholder={onIntake ? 'Ask Jet, or attach an invoice…' : 'Ask Jet about a demand…'}
            aria-label="Message Jet"
            disabled={pending}
          />
          <button type="submit" disabled={pending || !draft.trim()}>
            Send
          </button>
        </form>
      </section>
    </div>
  );
}
